import {
  KeyboardEvent,
  ReactNode,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { MasterIndexApi, MasterNameApi } from '@/api';
import { IcPopUp, IcShieldPerson } from '@/assets/images';
import { FlagChips, WantedPersonFlag } from '@/components';
import {
  IMasterName,
  IMasterNameOrganization,
  IMasterNamePerson,
  MasterNameType,
} from '@/models';
import { useToastStore } from '@/store';
import { colors } from '@/theme/variables';
import {
  formatPersonName,
  getMasterNameSecondaryInfo,
  openNewWindow,
} from '@/utils';

interface IMasterNameAutocompleteProps {
  excludedMasterNames?: Array<IMasterNamePerson | IMasterNameOrganization>;
  noResults?: ReactNode;
  disabled?: boolean;
  onChange: (value: IMasterName) => void;
  onKeyUp?: (e: KeyboardEvent) => void;
  filterByType?: MasterNameType | null;
  includeOrganizations?: boolean;
  showFlags?: boolean;
  addNewAction?: () => void;
  key?: number;
  value?: IMasterName;
  includeUnverified?: boolean;
  clearable?: boolean;
  onClear?: () => void;
}

export const MasterNameAutocomplete = ({
  excludedMasterNames = [],
  noResults,
  onChange,
  onKeyUp,
  disabled = false,
  addNewAction,
  filterByType,
  includeOrganizations = false,
  showFlags = true,
  key,
  value,
  includeUnverified = false,
  clearable = false,
  onClear,
}: IMasterNameAutocompleteProps) => {
  const { updateToast } = useToastStore();
  const [masterNames, setMasterNames] = useState<IMasterName[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const hasFetchedInitial = useRef(false);
  const fetchGenerationRef = useRef(0);

  const fetchMasterNames = async (search?: string) => {
    const thisGeneration = ++fetchGenerationRef.current;
    setLoading(true);
    try {
      const trimmedSearch = search?.trim();

      // Only add quickFilterValues if there's actual search text
      const quickFilterValues =
        trimmedSearch && trimmedSearch.length > 0
          ? trimmedSearch.split(/[\s,]+/).filter(Boolean)
          : undefined;

      const baseItems = includeUnverified
        ? []
        : [
            {
              field: 'isVerified',
              operator: 'equals',
              value: true,
            },
          ];
      const quickFilterProps =
        quickFilterValues && quickFilterValues.length > 0
          ? {
              quickFilterValues,
              quickFilterLogicOperator: 'and',
            }
          : {};

      if (!filterByType && includeOrganizations) {
        const [personResponse, organizationResponse] = await Promise.all([
          MasterIndexApi.getMasterPersons({
            page: 1,
            limit: 50,
            filter: JSON.stringify({
              items: [
                ...baseItems,
                {
                  field: 'isOrganization',
                  operator: 'equals',
                  value: false,
                },
              ],
              ...quickFilterProps,
            }),
            sort: JSON.stringify({ createdAt: -1 }),
          }),
          MasterIndexApi.getMasterPersons({
            page: 1,
            limit: 50,
            filter: JSON.stringify({
              items: [
                ...baseItems,
                {
                  field: 'isOrganization',
                  operator: 'equals',
                  value: true,
                },
              ],
              ...quickFilterProps,
            }),
            sort: JSON.stringify({ createdAt: -1 }),
          }),
        ]);

        if (thisGeneration !== fetchGenerationRef.current) return;

        setMasterNames([
          ...personResponse.data.docs,
          ...organizationResponse.data.docs,
        ]);
        return;
      }

      const typeFilter =
        filterByType === MasterNameType.PERSON
          ? {
              field: 'isOrganization',
              operator: 'equals',
              value: false,
            }
          : filterByType === MasterNameType.ORGANIZATION
          ? {
              field: 'isOrganization',
              operator: 'equals',
              value: true,
            }
          : null;

      const response = await MasterIndexApi.getMasterPersons({
        page: 1,
        limit: 50,
        filter: JSON.stringify({
          items: typeFilter ? [...baseItems, typeFilter] : baseItems,
          ...quickFilterProps,
        }),
        sort: JSON.stringify({ createdAt: -1 }),
      });

      if (thisGeneration !== fetchGenerationRef.current) return;

      setMasterNames(response.data.docs);
    } catch (err: any) {
      if (thisGeneration !== fetchGenerationRef.current) return;

      updateToast({
        open: true,
        message: err?.message || 'Failed to search names',
      });
    } finally {
      // A superseded request must not clear the spinner for the newest one.
      if (thisGeneration === fetchGenerationRef.current) {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (_: SyntheticEvent, v: string) => {
    const inputValue = v ?? '';
    setSearchText(inputValue); // immediate UI update
    debouncedFetch(inputValue); // debounced API call
  };

  const debouncedFetch = useDebouncedCallback(async (v: string) => {
    const trimmedValue = v.trim();
    await fetchMasterNames(trimmedValue);
  }, 500);

  const getRecordType = (
    option: IMasterName | { _id: string; name: string },
  ): MasterNameType | null => {
    if (option && typeof option === 'object' && 'type' in option) {
      return option.type as MasterNameType;
    }
    return null;
  };

  const handleAddMasterName = (
    selectedValue: IMasterName | { _id: string; name: string } | null,
  ) => {
    if (!selectedValue || selectedValue._id === 'add-new') {
      if (!selectedValue) onClear?.();
      return;
    }

    const selectedValueCopy = JSON.parse(JSON.stringify(selectedValue));
    const closeAndClear = () => {
      debouncedFetch.cancel();
      setSearchText('');
      setOpen(false);
    };

    closeAndClear();
    if (
      'type' in selectedValueCopy &&
      selectedValueCopy.type === MasterNameType.PERSON
    ) {
      const person = selectedValueCopy as IMasterNamePerson;
      if (!person.name && person.firstName) {
        person.name = formatPersonName(person);
      }
    }

    if (!('type' in selectedValueCopy) && selectedValueCopy._id) {
      selectedValueCopy.type = MasterNameType.ORGANIZATION;
    }

    const finalizeSelection = (valueToSelect: IMasterName) => {
      onChange(valueToSelect);
    };

    if ('type' in selectedValueCopy) {
      if (
        selectedValueCopy.type === MasterNameType.PERSON &&
        selectedValueCopy._id
      ) {
        MasterIndexApi.getMasterPerson(selectedValueCopy._id)
          .then((res) => {
            finalizeSelection(res.data);
          })
          .catch(() => {
            finalizeSelection(selectedValueCopy as IMasterName);
          });
      } else {
        finalizeSelection(selectedValueCopy as IMasterName);
      }
    } else {
      MasterNameApi.getMasterName(selectedValueCopy._id)
        .then((res) => {
          finalizeSelection(res.data);
        })
        .catch((err) => {
          if (!('type' in selectedValueCopy)) {
            selectedValueCopy.type = MasterNameType.ORGANIZATION;
          }
          finalizeSelection(selectedValueCopy as IMasterName);
          updateToast({ open: true, message: err.message });
        });
    }
  };

  const handleOpenMasterName = (
    selectedValue: IMasterName | { _id: string; name: string } | null,
  ) => {
    if (!selectedValue || selectedValue._id === 'add-new') return;
    const recordType = getRecordType(selectedValue);
    if (!recordType) return;

    const url =
      recordType === MasterNameType.PERSON
        ? `/master-index/persons/${selectedValue._id}/view/information`
        : `/master-index/organizations/${selectedValue._id}/view/information`;

    openNewWindow(url, selectedValue._id);
  };

  // Fetch initial data when dropdown opens (not on mount)
  const handleOpen = () => {
    if (!hasFetchedInitial.current) {
      hasFetchedInitial.current = true;
      fetchMasterNames('');
    }
  };

  useEffect(() => {
    // Only refetch when filterByType changes after initial open
    if (hasFetchedInitial.current) {
      fetchMasterNames(searchText);
    }
  }, [filterByType]);

  // Reset searchText when value is cleared
  useEffect(() => {
    if (!value) {
      debouncedFetch.flush();
      setSearchText('');
    }
  }, [value]);

  return (
    <Box onMouseEnter={handleOpen} className="contents">
      <Autocomplete
        key={key}
        autoHighlight
        fullWidth
        disabled={disabled}
        loading={loading}
        loadingText="Loading..."
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={(value || null) as any}
        inputValue={searchText}
        onInputChange={handleInputChange}
        options={masterNames}
        noOptionsText={noResults}
        isOptionEqualToValue={(option, selectedValue) => {
          if (!option || !selectedValue) return false;
          return option._id === selectedValue._id;
        }}
        onChange={(_, selectedValue) => handleAddMasterName(selectedValue)}
        filterOptions={(allMasterNames, state) => {
          const inputWords = state.inputValue
            .toLowerCase()
            .split(/[\s,]+/)
            .filter(Boolean);

          return allMasterNames.filter((item) => {
            const foundInExcluded = (excludedMasterNames || []).find(
              (excludedItem) => {
                return excludedItem?._id === item._id;
              },
            );
            const isUnknown =
              (item as IMasterNameOrganization).name?.includes('Unknown') ||
              (item as IMasterNamePerson).firstName?.includes('Unknown');

            if (foundInExcluded || isUnknown) return false;

            if (inputWords.length === 0) return true;

            // If any word looks like a phone number, trust the server's results
            // (server already applied phone wildcard matching via Atlas Search)
            const hasPhoneSearch = inputWords.some((word) => {
              const digits = word.replace(/\D/g, '');
              return digits.length >= 3 && /^[\d\s+\-().]+$/.test(word);
            });
            if (hasPhoneSearch) return true;

            const nameParts = [
              (item as IMasterNamePerson).firstName?.toLowerCase(),
              (item as IMasterNamePerson).middleName?.toLowerCase(),
              (item as IMasterNamePerson).lastName?.toLowerCase(),
              (item as IMasterNameOrganization).name?.toLowerCase(),
            ]
              .filter((part): part is string => Boolean(part))
              .flatMap((part) => part.split(/\s+/));

            return inputWords.every((word) =>
              nameParts.some((part) => part.startsWith(word)),
            );
          });
        }}
        getOptionLabel={(option) => {
          if (option._id === 'add-new') return '';

          return formatPersonName(option);
        }}
        renderOption={(
          props,
          option: IMasterName | { _id: string; name: string },
        ) => {
          if (option._id === 'add-new' && addNewAction) {
            return (
              <Stack justifyContent="flex-end" width="100%">
                <Button
                  onClick={() => addNewAction()}
                  variant="text"
                  color="error"
                >
                  + Add new
                </Button>
              </Stack>
            );
          }

          const recordType = getRecordType(option);
          const isPerson = recordType === MasterNameType.PERSON;
          const isWanted = (option as IMasterNamePerson).isWanted;
          const name = formatPersonName(option);
          const secondaryInfo = getMasterNameSecondaryInfo(option);
          const isEmployee =
            isPerson && (option as IMasterNamePerson).isEmployee;
          const SecondaryIcon = isPerson
            ? PersonOutlineIcon
            : BusinessOutlinedIcon;

          const nameSection = (
            <Stack spacing={0.25}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  fontWeight={600}
                  sx={{ fontSize: 15, lineHeight: 1.2 }}
                >
                  {name}
                </Typography>
                {showFlags &&
                  isPerson &&
                  (() => {
                    const personOption = option as IMasterNamePerson;
                    return personOption.flags &&
                      personOption.flags.length > 0 ? (
                      <FlagChips
                        flags={personOption.flags.map((f) => f.flag)}
                        size="small"
                      />
                    ) : null;
                  })()}
                {showFlags && isWanted && <WantedPersonFlag size="small" />}
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                {isEmployee ? (
                  <IcShieldPerson
                    style={{
                      color: colors.grey[80],
                      width: 15,
                      height: 15,
                    }}
                  />
                ) : (
                  <SecondaryIcon
                    sx={{
                      color: 'text.secondary',
                      width: 15,
                      height: 15,
                    }}
                  />
                )}
                {secondaryInfo ? (
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: 13,
                      lineHeight: 1.15,
                    }}
                  >
                    {secondaryInfo}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          );

          const optionContent = (
            <Stack direction="row" justifyContent="space-between" width="100%">
              <Stack direction="column" spacing={0.75} width="100%" pr={1.5}>
                {nameSection}
              </Stack>
              <Stack
                direction="row"
                spacing={1}
                gap="8px"
                paddingRight="2px"
                alignItems="center"
                flexShrink={0}
              >
                <Button
                  type="button"
                  variant="outlined"
                  color="inherit"
                  size="small"
                  sx={{ minWidth: 78, height: 36 }}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleAddMasterName(option);
                  }}
                >
                  Add
                </Button>

                <IconButton
                  aria-label="open-master-name"
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleOpenMasterName(option);
                  }}
                >
                  <IcPopUp />
                </IconButton>
              </Stack>
            </Stack>
          );

          return (
            <Box
              component="li"
              {...props}
              onClick={() => {
                handleAddMasterName(option);
              }}
              key={option._id}
              sx={{
                flexDirection: 'row',
                alignItems: 'center',
                borderBottom: `1px solid ${colors.grey[10]}`,
                margin: 0,
                minHeight: secondaryInfo ? '54px' : '46px',
                padding: secondaryInfo ? '7px 12px' : '6px 12px',
                width: '100%',
              }}
            >
              {optionContent}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            value={searchText}
            placeholder="Search names..."
            onKeyUp={(e) => {
              if (onKeyUp) {
                onKeyUp(e);
              }
            }}
            sx={{
              '& .MuiInputBase-root': {
                width: '100% !important',
              },
            }}
            fullWidth
          />
        )}
        disableClearable={!clearable}
        ListboxProps={{
          style: {
            maxWidth: '100%',
            overflowX: 'hidden',
            cursor: 'default',
          },
        }}
      />
    </Box>
  );
};
